import { useCallback, useEffect, useMemo, useState } from "react"
import { Configuration } from "shared/configuration"
import { ConfigurationThingTypeMrn } from "shared/configurationData"
import { LockerDoorReservationSession } from "shared/lockerDoorReservationSession"
import { Reservation } from "shared/reservation"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import { SstDoor } from "shared/sst"
import { useAppSelector } from "src/app/hooks"
import { useGetThingsQuery } from "src/features/things/thingsApi"
import { selectThingsByThingTypeMrnFilter } from "src/features/things/thingsSlice"
import { isReservationSessionActive } from "../reservationSession"
import { parseSst2Utc } from "../sst2DateUtils"

type UseReservationProps = {
  reservation?: Reservation
  remainingTimeWarningThreshold?: number
  session?: LockerDoorReservationSession
  door?: SstDoor
  hasDoorLink?: boolean
}
export const useReservation = ({
  reservation,
  session,
  door,
  hasDoorLink,
}: UseReservationProps) => {
  const [now, setNow] = useState(new Date())

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNow(new Date())
    }, 1000)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  const { isLoading: isLoadingConfig, refetch: refetchConfig } =
    useGetThingsQuery(
      {
        params: {
          "ap.name": SharedAccessPatternOsEnum.GetConfigurationsByTenant,
          "ap.tenantId": reservation?.tenantId,
          "ap.refNames[0]": "configAboutToExpirationReminderDelayMinutes",
          tenantId: reservation?.tenantId,
        },
      },
      {
        skip: !reservation?.tenantId,
      },
    )

  const configFilter = useCallback(
    (thing: Configuration) =>
      thing.tenantId === reservation?.tenantId &&
      thing.refName === "configAboutToExpirationReminderDelayMinutes",
    [reservation?.tenantId],
  )
  const configAboutToExpirationReminderDelayMinutes = useAppSelector((s) =>
    selectThingsByThingTypeMrnFilter(
      s,
      ConfigurationThingTypeMrn,
      configFilter,
    ),
  )[0] as unknown as Configuration

  const {
    expiresIn,
    showExpirationWarning,
    hasExpired,
    expirationDate,
    hasReservation,
    statusColor,
    isReserving,
    statusTextColor,
    hasPayment,
    isCompleted,
    hasDeposited,
    hasPickedUp,
    createdSince,
  } = useMemo(() => {
    let expiresIn = "N/A"

    let showExpirationWarning = false

    let hasExpired = false

    let hasReservation =
      hasDoorLink &&
      reservation &&
      reservation.sstReservation &&
      !["DELETED", "CLOSE", "CLOSED", ""].includes(
        reservation?.sstReservation?.status || "",
      )

    let isReserving = false

    // dont show the reservation session if there is a reservation
    if (session && !hasReservation) {
      isReserving = isReservationSessionActive(session, Date.now())
    }

    // LOCKER-442: SST v2 stores `expiration_time` as naive UTC (`T` or space
    // separated); `new Date()` would read it as browser-local, 4-5h late in
    // America/Toronto. ISO-Z (v1) rows pass through unchanged.
    const expirationDate =
      parseSst2Utc(reservation?.sstReservation?.expiration_time) ??
      new Date(8640000000000000)

    const diffInSeconds = (expirationDate.getTime() - now.getTime()) / 1000
    const unsignedDiffInSeconds = Math.abs(diffInSeconds)

    const days = Math.floor(unsignedDiffInSeconds / 86400)
    const hours = Math.floor((unsignedDiffInSeconds % 86400) / 3600)
    const minutes = Math.floor((unsignedDiffInSeconds % 3600) / 60)
    // expiresIn 'smart' formatting. display only days & hours if more than 1 day left
    if (days > 0) {
      expiresIn = `${days}j ${hours}h`
    } else if (hours > 0) {
      // display hour and minutes if less than 1 day left
      expiresIn = `${hours}h ${minutes}m`
    } else {
      // display only minutes and seconds  if less than 1 hour left
      expiresIn = `${minutes}m`
    }

    let createdSince = "-"
    const diff = Math.abs(
      now.getTime() -
        new Date(
          (reservation?.sstReservation?._created as string) ||
            (reservation?.createdAt as string),
        ).getTime(),
    )
    const diffDays = Math.floor(diff / 86400000)
    const diffHours = Math.floor((diff % 86400000) / 3600000)
    const diffMinutes = Math.floor((diff % 3600000) / 60000)

    if (diffDays > 0) {
      createdSince = `${diffDays}j ${diffHours}h`
    } else if (diffHours > 0) {
      createdSince = `${diffHours}h ${diffMinutes}m`
    } else {
      createdSince = `${diffMinutes}m`
    }

    const sstStatus = reservation?.sstReservation?.status || ""
    // Use status as primary signal for lifecycle state.
    // Timestamps alone are unreliable — V2 mapper may have set reception_time
    // prematurely on older reservations.
    const terminalStatuses = ["CLOSE", "CLOSED", "CANCEL", "CANCELLED", "DELETED"]
    const depositedStatuses = ["DELIVERED", "EXPIRED", ...terminalStatuses]
    const pickedUpStatuses = ["CLOSE", "CLOSED"]

    const isCompleted = pickedUpStatuses.includes(sstStatus)

    // LOCKER-402: a collected parcel is not "expired", whatever the clock says.
    //
    // `isCompleted` is computed BEFORE these two on purpose — it used to be
    // computed after, and the colour ternary below was the only place it was
    // consulted. So `hasExpired` stayed true for a parcel the customer had
    // already taken home, and every OTHER reader of the flag was wrong:
    // `OsLockerDoor.tsx:292` tagged the tile `uf-door-expired`, and the shared
    // drawer offered an expiration-recovery code for an empty door.
    //
    // Guarding the flag at its source rather than at each reader is what makes
    // V1 and V2 agree by construction: `computeSst2DoorState` (the V2 port)
    // applies the identical guard, and `useReservation.parity.test.tsx` asserts
    // the two produce the same flags over the same case table. Two parallel
    // gates would have drifted the moment one of them gained a case.
    //
    // `CANCEL` / `CANCELLED` are deliberately NOT in `pickedUpStatuses`
    // (LOCKER-391 — the parcel is still behind the door), so they keep expiring.
    //
    // The `!hasExpired` guard on the warning is the SECOND divergence the parity
    // test found, and it was pre-existing: an expired row has a negative
    // `diffInSeconds`, which is trivially "less than the warning window", so V1
    // reported a row as expired AND about-to-expire at once while V2 reported
    // only expired. Nobody noticed because every reader compensates by hand —
    // `OsLockerDoor.tsx:293` and `OsLockerDoorV2.tsx:302` both write
    // `showExpirationWarning && !hasExpired`, and the two colour ternaries below
    // test `hasExpired` first. Guarding it here is therefore a no-op at every
    // existing call site and removes the trap for the next one.
    //
    // Order matters and mirrors `computeSst2DoorState`: isCompleted, then
    // hasExpired, then the warning, each narrowing the one before it.
    hasExpired = !isCompleted && diffInSeconds <= 0
    showExpirationWarning =
      !isCompleted &&
      !hasExpired &&
      diffInSeconds <
        (configAboutToExpirationReminderDelayMinutes?.nVal || 15) * 60
    // if (hasExpired) {
    //   expiresIn = `-${expiresIn}`
    // }

    let statusColor = isCompleted
      ? "#43A047"
      : isReserving
      ? "lightblue"
      : hasExpired
      ? "#FF6B6B"
      : showExpirationWarning
      ? "orange"
      : !hasReservation
      ? "#43A047"
      : "white"

    const statusTextColor = isCompleted
      ? "whitesmoke"
      : isReserving
      ? "primary.main"
      : hasExpired
      ? "whitesmoke"
      : showExpirationWarning
      ? "whitesmoke"
      : !hasReservation
      ? "whitesmoke"
      : "primary.main"
    return {
      expiresIn,
      expirationDate,
      showExpirationWarning,
      hasExpired,
      hasReservation,
      statusColor,
      isReserving,
      statusTextColor,
      hasPayment: !!reservation?.amountToCollect
        ? reservation?.amountToCollect > 0
        : false,
      isCompleted,
      hasDeposited: depositedStatuses.includes(sstStatus) ||
        !!reservation?.sstReservation?.delivery_time,
      hasPickedUp: pickedUpStatuses.includes(sstStatus),
      createdSince,
    }
  }, [
    session,
    hasDoorLink,
    reservation,
    now,
    configAboutToExpirationReminderDelayMinutes?.nVal,
  ])

  return {
    expiresIn,
    expirationDate,
    showExpirationWarning,
    hasExpired,
    statusColor,
    hasReservation,
    isReserving,
    statusTextColor,
    hasPayment,
    isCompleted,
    hasDeposited,
    hasPickedUp,
    createdSince,
  }
}
